import React, { SFC, useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
    makeStyles,
    Drawer,
    Grid,
    Typography,
    IconButton,
    CircularProgress,
    Link,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Menu,
    ListItemIcon,
    MenuItem,
    ListItemText,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import { ExpandableText } from '../../../shared/components/ExpandableText'
import { useTranslation } from 'react-i18next'
import ChargerList from './ChargerList'
import { LocationInfo, Location } from '../../../shared/types/location'
import { Charger } from '../../../shared/types/charger'
import { useCalcWidthToQuarter } from '../../../utils/ui'
import LocationWhitelistEditDialog from '../locationWhitelistEditDialog/LocationWhitelistEditDialog'
import LocationAddEditDialog from '../locationAddEditDialog/LocationAddEditDialog'
import ChargerAddEditDialog from '../chargerAddEditDialog/ChargerAddEditDialog'
import ChargerSettingsDialog from '../chargerSettingsDialog/ChargerSettingsDialog'
import ChargerQRCodeDialog from '../chargerQRCodeDialog/ChargerQRCodeDialog'
import { DeleteConfirmationDialog } from '../../../shared/components/deleteConfirmationDialog'
import ImagesManager from '../../../shared/components/imagesManager'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import PersonIcon from '@material-ui/icons/Person'
import AttachMoneyIcon from '@material-ui/icons/AttachMoney'
import MoreVertIcon from '@material-ui/icons/MoreVert';
import EvStationIcon from "@material-ui/icons/EvStation";
import {
    deleteLocation,
    uploadPhotosForLocation,
    deleteLocationPhoto,
    deleteCharger,
    fetchLocationForDetail,
} from '../../../redux/actions/locations'
import { promiseError } from '../../../shared/redux/actions/common'
import { ReadOnlyEditor } from '../../../shared/components/ReadOnlyEditor'
import { updateLocationWhitelist } from '../../../redux/actions/locations'
import { config } from '../../../config'
import LocationPricingConditionsDialog from '../locationPricingConditionsDialog/LocationPricingConditionsDialog'
import FlexiChargingSetupDialog from '../flexiChargingSetupDialog/FlexiChargingSetupDialog'

const useStyles = makeStyles((theme) => ({
    drawerPaper: {
        top: '64px',
        height: 'calc(100% - 64px)',
    },
    contents: {
        padding: theme.spacing(3, 2),
    },
    contentsProgress: {
        display: 'flex',
        height: '100%',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    imagesManagerWrapper: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    title: {
        display: 'flex',
        [theme.breakpoints.down('md')]: {
            display: 'block',
        },
    },
    titleInfo: {
        flex: '1 0 auto',
        width: 'calc(100% - 88px)',
    },
    titleText: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    locationImage: {
        width: '100%',
        height: theme.spacing(10),
    },
    locationField: {
        textAlign: 'left',
        marginBottom: theme.spacing(2),
    },
    chargersCount: {
        textAlign: 'left',
        alignItems: 'center',
        paddingBottom: theme.spacing(1),
    },
    addCharger: {
        textAlign: 'right',
        paddingBottom: theme.spacing(1),
    },
    detailLabel: {
        textAlign: 'left',
        display: 'flex',
    },
    detailValue: {
        textAlign: 'right',
    },
    accordion: {
        boxShadow: 'none',
    },
    accordionSummary: {
        padding: 0,
        minHeight: 0,
        '& .MuiAccordionSummary-content': {
            margin: 0,
        },
        '& .MuiAccordionSummary-expandIcon': {
            padding: 0,
        },
    },
    accordionDetails: {
        padding: 0,
    },
    dropdownIcon: {
        minWidth: '32px',
    }
}))

interface LocationDetailProps {
    onClose: Function
    locationId?: string
}
const EMPTY_LOCATION = { images: [] } as any

const LocationDetail: SFC<LocationDetailProps> = ({ onClose, locationId }) => {
    const classes = useStyles()
    const { t } = useTranslation()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const calcWidthToQuarter = useCalcWidthToQuarter('locationDrawerPaper')

    const dispatch = useDispatch()

    const locationDetail = useSelector((state: any) => state.locations.detail.value)
    const locationDetailExecuted = useSelector((state: any) => state.locations.detail.executed)

    const [dialogOpen, setDialogOpen] = useState({
        chargerSettings: false,
        editLocation: false,
        editLocationWhitelist: false,
        editFlexiCharging: false,
        QRCode: false,
        deleteLocation: false,
        showPricingConditions: false,
    })
    const [dialogModel, setDialogModel] = useState<any>()

    const [initialLoadInProgress, setInitialLoadInProgress] = useState<boolean>(false)
    const [locationFromEdit, setLocationFromEdit] = useState<LocationInfo>()

    const prepareFullAdress = (index: any) => {
        if (index === 0) {
            return (
                ', ' + locationToDisplay.street + ' ' + locationToDisplay.houseNum + ', ' + locationToDisplay.postalCode
            )
        } else {
            return ''
        }
    }

    const deleteExecuted = useSelector((state: any) => state.locations.delete.executed)

    const chargers = useSelector((state: any) => state.locations.editedLocationChargers.chargers)
    const [chargerAddEditState, setChargerAddEditState] = useState({
        open: false,
        isAdd: false,
        initialModel: {} as Charger,
    })

    const [chargerToDelete, setChargerToDelete] = useState<Charger>()
    const chargerDeleteExecuted = useSelector((state: any) => state.locations.chargers.delete.executed)

    const onEditDialogClose = (editedLocation?: Location) => {
        setDialogOpen({ ...dialogOpen, editLocation: false })
        if (locationDetail && editedLocation) {
            setLocationFromEdit({
                ...locationDetail,
                ...editedLocation,
            })
        }
    }

    const onPricingConditionClose = () => {
        setDialogOpen({ ...dialogOpen, showPricingConditions: false })
    }

    const onEditWhitelistDialogClose = (whitelist?: string[]) => {
        setDialogOpen({ ...dialogOpen, editLocationWhitelist: false })
        if (whitelist) {
            dispatch(updateLocationWhitelist(locationDetail.id, whitelist))
        }
    }

    const onEditFlexiChargingDialogClose = (editedLocation?: Location) => {
        setDialogOpen({ ...dialogOpen, editFlexiCharging: false })

        if (locationDetail && editedLocation) {
            const newLocationData = {
                ...locationDetail,
                ...editedLocation,
            };

            if (editedLocation.chargers) {
                setLocationFromEdit(newLocationData);
            } else {
                // Ak editedLocation obsahuje len flexi nastavenia, 
                // ale nie pole chargerov, musíš ich "zlúčiť" manuálne.
                // Toto závisí od toho, čo presne ti vracia ten Flexi dialóg.
                setLocationFromEdit(newLocationData);
            }
            if (locationId) {
                dispatch(fetchLocationForDetail(locationId));
            }
        }
    }

    const onSettingsDialogClose = () => {
        setDialogOpen({ ...dialogOpen, chargerSettings: false })
    }

    const onQRCodeDialogClose = () => {
        setDialogOpen({ ...dialogOpen, QRCode: false })
    }

    const onChargerAddEditDialogClose = (charger?: Charger) => {
        setChargerAddEditState({
            ...chargerAddEditState,
            open: false,
        });
        if (charger && locationId) {
            dispatch(fetchLocationForDetail(locationId));
        }
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };
    const locationToDisplay: LocationInfo = locationFromEdit ?? locationDetail ?? EMPTY_LOCATION

    const totalCount = locationToDisplay.chargers?.length ?? 0;
    const inUseCount = locationToDisplay.connectedChargers?.length ?? 0;

    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)

    const tenants = useSelector((state: any) => state.tenants.value)
    const tenant = tenants?.find((tenant: any) => tenant.id === locationToDisplay.tenantId);

    const locationFields = [
        [t('locations.address'), locationToDisplay.city],
        tenantId && tenantId === config.mainTenantId ? [t('common.tenant'), tenant?.name ? tenant.name : locationToDisplay.tenantId] : [],
        [t('common.description'), locationToDisplay.description],
    ]

    const handleRefreshData = useCallback(async () => {
        if (locationToDisplay?.id && tenantId) {
            try {
                // Použijeme Promise.all, aby sme počkali na reálne stiahnutie dát z oboch API,
                // ale so SPRÁVNYMI parametrami (prázdny string pre search, potom tenantId).
                await Promise.all([
                    dispatch(fetchLocationForDetail(locationToDisplay.id))
                ])
            } catch (error) {
                console.error("Chyba pri obnove dát:", error)
            }
        }
    }, [dispatch, locationToDisplay?.id, tenantId])

    useEffect(() => {
        if (locationId && (!locationDetail || locationId !== locationDetail.id) && !initialLoadInProgress) {
            setInitialLoadInProgress(true)
            dispatch(fetchLocationForDetail(locationId))
        }
    }, [dispatch, locationId, locationDetail, initialLoadInProgress])

    useEffect(() => {
        if (locationDetailExecuted) {
            setInitialLoadInProgress(false)
        }
    }, [locationDetailExecuted])

    useEffect(() => {
        if (locationId && locationFromEdit && locationFromEdit.id !== locationId) {
            setLocationFromEdit(undefined)
        }
    }, [locationId, locationFromEdit])

    useEffect(() => {
        if (locationDetail) {
            setLocationFromEdit(locationDetail);
        }
    }, [locationDetail]);

    return (
        <>
            <Drawer
                open={Boolean(locationId)}
                PaperProps={{
                    elevation: 8,
                    ref: calcWidthToQuarter,
                    id: 'locationDrawerPaper',
                    className: classes.drawerPaper,
                }}
                anchor="right"
                variant="persistent"
            >
                {initialLoadInProgress ? (
                    <div className={classes.contentsProgress}>
                        <CircularProgress />
                    </div>
                ) : (

                    <Grid container spacing={0} className={classes.contents}>
                        <Grid item xs={12} className={classes.title}>
                            <div className={classes.titleInfo}>
                                <Typography className={classes.titleText} variant="h5">{locationToDisplay.name}</Typography>
                                <Typography component="div" variant="caption">
                                    {t('locations.chargersAvailable', {
                                        countTotal: totalCount,
                                        countAvailable: inUseCount, // This calculation is now safe
                                    })}
                                </Typography>
                            </div>
                            <div>
                                <IconButton onClick={handleMenuOpen}>
                                    <MoreVertIcon fontSize="small" />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            setDialogOpen({ ...dialogOpen, showPricingConditions: true });
                                            handleMenuClose();
                                        }}
                                    >
                                        <ListItemIcon className={classes.dropdownIcon}><AttachMoneyIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText primary="Pricing" />
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            setDialogOpen({ ...dialogOpen, editLocationWhitelist: true });
                                            handleMenuClose();
                                        }}
                                    >
                                        <ListItemIcon className={classes.dropdownIcon}><PersonIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText primary="Whitelist" />
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            setDialogOpen({ ...dialogOpen, editFlexiCharging: true });
                                            handleMenuClose();
                                        }}
                                    >
                                        <ListItemIcon className={classes.dropdownIcon}><EvStationIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText primary="Flexi Charging" />
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            setDialogOpen({ ...dialogOpen, editLocation: true });
                                            handleMenuClose();
                                        }}
                                    >
                                        <ListItemIcon className={classes.dropdownIcon}><EditIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText primary="Edit" />
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            setDialogOpen({ ...dialogOpen, deleteLocation: true });
                                            handleMenuClose();
                                        }}
                                        style={{ color: 'red' }} // zvýraznenie Delete
                                    >
                                        <ListItemIcon className={classes.dropdownIcon}><DeleteIcon fontSize="small" style={{ color: 'red' }} /></ListItemIcon>
                                        <ListItemText primary="Delete" />
                                    </MenuItem>
                                </Menu>

                                <IconButton onClick={() => onClose()}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </div>
                        </Grid>
                        <Grid item xs={12} className={classes.imagesManagerWrapper}>
                            <ImagesManager
                                images={locationToDisplay.images}
                                imageClassName={classes.locationImage}
                                imageCols={4}
                                uploadAction={async (files) => {
                                    await dispatch(uploadPhotosForLocation(locationToDisplay, files))
                                }}
                                uploadError={() => dispatch(promiseError(t('common.uploadError')))}
                                uploadProgressStateSelector={(state: any) => state.locations.uploadPhotos}
                                deleteAction={async (image) =>
                                    await dispatch(deleteLocationPhoto(locationToDisplay, image))
                                }
                                deleteProgressStateSelector={(state: any) => state.locations.deletePhoto}
                            />
                        </Grid>
                        {locationFields.map(
                            (field, index) =>
                                field[1] && (
                                    <Grid key={index} item container xs={12} className={classes.locationField}>
                                        {field[0] === t('common.description') ? (
                                            <Accordion className={classes.accordion}>
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon />}
                                                    className={classes.accordionSummary}
                                                >
                                                    <Grid item xs={12} className={classes.detailLabel}>
                                                        <Typography variant="subtitle2">{field[0]}</Typography>
                                                    </Grid>
                                                </AccordionSummary>
                                                <AccordionDetails className={classes.accordionDetails}>
                                                    <Typography align="justify" variant="caption" noWrap={false}>
                                                        <ReadOnlyEditor value={field[1]} />
                                                    </Typography>
                                                </AccordionDetails>
                                            </Accordion>
                                        ) : (
                                            <>
                                                <Grid item xs={12} className={classes.detailLabel}>
                                                    <Typography variant="subtitle2">{field[0]}</Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <ExpandableText
                                                        typographyProps={{
                                                            align: 'justify',
                                                            variant: 'caption',
                                                        }}
                                                    >
                                                        {field[1] + prepareFullAdress(index)}
                                                    </ExpandableText>
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                )
                        )}
                        <Grid item xs={6} className={classes.chargersCount}>
                            <Typography variant="caption">
                                {t('locations.chargersCountSimple', {
                                    count: totalCount,
                                })}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} className={classes.addCharger}>
                            <Link
                                component="button"
                                onClick={() => {
                                    setChargerAddEditState({
                                        open: true,
                                        isAdd: true,
                                        initialModel: {
                                            locationId: locationToDisplay.id,
                                            tenantId: locationToDisplay.tenantId
                                        } as Charger,
                                    })
                                }}
                            >
                                {t('locations.addCharger')}
                            </Link>
                        </Grid>
                        <Grid item xs={12}>
                            <ChargerList
                                chargers={chargers}
                                onDelete={setChargerToDelete}
                                onEdit={(chargerToEdit) => {
                                    setChargerAddEditState({
                                        open: true,
                                        isAdd: false,
                                        initialModel: {
                                            ...chargerToEdit,
                                            tenantId: locationToDisplay?.tenantId || ''
                                        }
                                    })
                                }}
                                onSettings={(charger) => {
                                    setDialogModel(charger)
                                    setDialogOpen({ ...dialogOpen, chargerSettings: true })
                                }}
                                onQRCode={(charger) => {
                                    setDialogModel(charger)
                                    setDialogOpen({ ...dialogOpen, QRCode: true })
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
            </Drawer>

            <ChargerAddEditDialog onClose={onChargerAddEditDialogClose} {...chargerAddEditState} />
            {dialogOpen.chargerSettings && (
                <ChargerSettingsDialog open={true} onClose={onSettingsDialogClose} initialModel={dialogModel} onRefresh={handleRefreshData} />
            )}
            {dialogOpen.QRCode && (
                <ChargerQRCodeDialog open={true} onClose={onQRCodeDialogClose} initialModel={dialogModel} />
            )}

            <FlexiChargingSetupDialog
                open={dialogOpen.editFlexiCharging}
                onClose={onEditFlexiChargingDialogClose}
                initialModel={locationToDisplay}
            />
            <LocationWhitelistEditDialog
                open={dialogOpen.editLocationWhitelist}
                onClose={onEditWhitelistDialogClose}
                initialModel={locationToDisplay}
            />
            <LocationPricingConditionsDialog
                open={dialogOpen.showPricingConditions}
                onClose={onPricingConditionClose}
            />
            <LocationAddEditDialog
                open={dialogOpen.editLocation}
                isAdd={false}
                onClose={onEditDialogClose}
                initialModel={locationToDisplay}
            />
            <DeleteConfirmationDialog
                open={dialogOpen.deleteLocation}
                onConfirm={async () => {
                    await dispatch(deleteLocation(locationToDisplay))
                    setDialogOpen({ ...dialogOpen, deleteLocation: false })
                }}
                onCancel={() => setDialogOpen({ ...dialogOpen, deleteLocation: false })}
                inProgress={!deleteExecuted}
            />
            <DeleteConfirmationDialog
                open={Boolean(chargerToDelete)}
                onConfirm={async () => {
                    const chargerToDeleteDefined = chargerToDelete as Charger
                    await dispatch(deleteCharger(chargerToDeleteDefined))
                    await dispatch(fetchLocationForDetail(locationToDisplay.id as string))
                    setChargerToDelete(undefined)
                }}
                onCancel={() => setChargerToDelete(undefined)}
                inProgress={!chargerDeleteExecuted}
            />
        </>
    )
}

export default LocationDetail
